import React from 'react';
import { graphql } from 'gatsby';
import { breakpoints } from '../../primitives/tokens';
import NotchedSlider, { NotchedSliderInstance } from '../../primitives/notched-slider';
import Pagination from '../../primitives/notched-slider/pagination';
import Typography from '../../primitives/typography';
import { colors } from '../../primitives/colors';
import Divider from '../../primitives/divider';
import Container from '../../primitives/grid/container';
import paddingDefault from '../../utils/padding-default';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import Image from '../../primitives/image';

const MediaShowcaseSlider: React.FC<Queries.MediaShowcaseSliderDataFragment> = (data): JSX.Element => {
    const { heading, slides } = data;

    return (
        <NotchedSlider slideCount={(slides || []).length}>
            <Divider />
            <div css={[{ position: 'relative' }, paddingDefault()]}>
                <Container>
                    <Typography
                        as="h2"
                        fontWeight="medium"
                        fontSize="3xl"
                        css={[{
                            color: colors.shadesBlack,
                            textAlign: 'center',
                            marginBottom: '32px',
                            [breakpoints.tb]: {
                                textAlign: 'center',
                            },
                        }]}
                    >
                        {apCaseOnlyTitleTags(heading)}
                    </Typography>
                </Container>
                <NotchedSliderInstance slidesPerView={{
                    dt: 1,
                }}
                >
                    <div css={{ position: 'relative' }}>
                        <NotchedSlider.Slides>
                            {(slides || []).map((slide, slideIndex) => {
                                if (!slide) return null;

                                const { heading: slideHeading, copy, image, video } = slide;
                                // Only eagerly load the first slide image (context will handle this for early blocks)
                                const isFirstSlide = slideIndex === 0;

                                return (
                                    <div css={{ position: 'relative' }} key={slide.heading}>
                                        <div css={{ position: 'relative', margin: 'auto', maxWidth: '1100px' }}>
                                            <div css={{
                                                position: 'relative',
                                                marginBottom: '24px',
                                                width: '100%',
                                                '&:before': {
                                                    content: '""',
                                                    display: 'block',
                                                    paddingTop: '56%',
                                                },
                                                '& .media': {
                                                    position: 'absolute',
                                                    top: '0',
                                                    left: '0',
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                },
                                            }}
                                            >
                                                {video?.url && (
                                                    // eslint-disable-next-line jsx-a11y/media-has-caption
                                                    <video className="media" controls>
                                                        <source src={video?.url} type="video/mp4" />
                                                    </video>
                                                )}
                                                {image?.url && (
                                                    <Image
                                                        image={image}
                                                        alt={image.description || ''}
                                                        className="media"
                                                        loading={isFirstSlide ? undefined : 'lazy'}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <Container>
                                            <Typography as="h3" css={{ marginBottom: '12px' }} fontWeight="medium">
                                                {slideHeading}
                                            </Typography>
                                            <Typography css={{ maxWidth: '800px' }}>
                                                {copy}
                                            </Typography>
                                        </Container>
                                    </div>
                                );
                            })}
                        </NotchedSlider.Slides>
                    </div>
                    {slides && (
                        <Container>
                            <Pagination />
                        </Container>
                    )}
                </NotchedSliderInstance>
            </div>
            <Divider />
        </NotchedSlider>
    );
};

export default MediaShowcaseSlider;

export const query = graphql`
    fragment MediaShowcaseSliderData on CsMediaShowcaseGroup {
        heading
        slides {
            heading
            copy
            image {
                filename
                title
                url
                contentStackAsset(size: FULL_MAX_WIDTH)
                description
            }
            video {
                title
                url
            }
        }
    }
`;
